import React, { useState } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import api from "../api";
import { useApp } from "../AppContext";
import { toast } from "sonner";
import DevBranding from "../components/DevBranding";

const AdminLogin = () => {
    const { t } = useApp();
    const navigate = useNavigate();
    // Tenant-scoped login: route may be /r/:slug/admin/login. When a slug is
    // present, the login form asks for email + password (multi-user tenant
    // team). Otherwise it's the legacy single-admin login (username/pin).
    const { slug } = useParams();
    const isTenantScoped = Boolean(slug);
    const [mode, setMode] = useState(isTenantScoped ? "team" : "password"); // password | pin | team
    const [form, setForm] = useState({ username: "admin", password: "", pin: "", email: "" });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            let body;
            if (mode === "team" && isTenantScoped) {
                body = { tenant_slug: slug, email: form.email, password: form.password };
            } else if (mode === "password") {
                body = { username: form.username, password: form.password };
            } else {
                body = { pin: form.pin };
            }
            const res = await api.post("/admin/login", body);
            // Cookie is now set httpOnly by the server. Clean up any legacy
            // localStorage token from the old client (no longer used by api.js).
            try { localStorage.removeItem("admin_token"); } catch (err) {
                console.debug("localStorage cleanup failed:", err);
            }
            // Track success outcome only (no token stored client-side).
            void res;
            toast.success(t.common.success);
            navigate("/admin");
        } catch (err) {
            toast.error(err?.response?.data?.detail || t.admin.invalid);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            data-testid="admin-login-page"
            className="min-h-screen flex flex-col items-center justify-center bg-earth-100 grain p-6"
        >
            <div className="w-full max-w-md bg-white border border-earth-200 rounded-[28px] shadow-xl p-10">
                <Link
                    to="/"
                    className="text-xs uppercase tracking-wider text-earth-500 hover:text-terracotta-600"
                >
                    ← Inicio
                </Link>
                <div className="w-14 h-14 rounded-full bg-terracotta-500 flex items-center justify-center text-white mt-6 mb-5">
                    <Lock className="w-6 h-6" />
                </div>
                <h1 className="font-serif font-black text-3xl text-earth-900">
                    {t.admin.loginTitle}
                </h1>
                <p className="text-sm text-earth-600 mt-2">
                    {isTenantScoped ? (
                        <span className="font-mono text-xs uppercase tracking-wider bg-earth-100 rounded px-2 py-0.5">
                            {slug}
                        </span>
                    ) : "Delicias Dominicanas"}
                </p>

                {isTenantScoped ? (
                    <div className="mt-6 text-xs bg-terracotta-50 border border-terracotta-200 rounded-xl px-3 py-2 text-terracotta-900">
                        Team login — use the email and password from your invite.
                    </div>
                ) : (
                <div className="mt-6 flex gap-2 bg-earth-100 rounded-full p-1">
                    <button
                        type="button"
                        data-testid="admin-mode-password-btn"
                        onClick={() => setMode("password")}
                        className={`flex-1 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                            mode === "password"
                                ? "bg-white text-earth-900 shadow-sm"
                                : "text-earth-600"
                        }`}
                    >
                        {t.admin.password}
                    </button>
                    <button
                        type="button"
                        data-testid="admin-mode-pin-btn"
                        onClick={() => setMode("pin")}
                        className={`flex-1 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                            mode === "pin"
                                ? "bg-white text-earth-900 shadow-sm"
                                : "text-earth-600"
                        }`}
                    >
                        {t.admin.pin}
                    </button>
                </div>
                )}

                <form onSubmit={submit} className="mt-6 space-y-4">
                    {mode === "team" && isTenantScoped ? (
                        <>
                            <div>
                                <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-2">Email</label>
                                <input
                                    data-testid="admin-team-email-input"
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="w-full bg-earth-50 border border-earth-200 rounded-xl px-4 py-3 outline-none focus:border-terracotta-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-2">{t.admin.password}</label>
                                <input
                                    data-testid="admin-team-password-input"
                                    type="password"
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    className="w-full bg-earth-50 border border-earth-200 rounded-xl px-4 py-3 outline-none focus:border-terracotta-500"
                                    required
                                />
                            </div>
                        </>
                    ) : mode === "password" ? (
                        <>
                            <div>
                                <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-2">
                                    {t.admin.username}
                                </label>
                                <input
                                    data-testid="admin-username-input"
                                    value={form.username}
                                    onChange={(e) =>
                                        setForm({ ...form, username: e.target.value })
                                    }
                                    className="w-full bg-earth-50 border border-earth-200 rounded-xl px-4 py-3 outline-none focus:border-terracotta-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-2">
                                    {t.admin.password}
                                </label>
                                <input
                                    data-testid="admin-password-input"
                                    type="password"
                                    value={form.password}
                                    onChange={(e) =>
                                        setForm({ ...form, password: e.target.value })
                                    }
                                    className="w-full bg-earth-50 border border-earth-200 rounded-xl px-4 py-3 outline-none focus:border-terracotta-500"
                                />
                            </div>
                        </>
                    ) : (
                        <div>
                            <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-2">
                                {t.admin.pin}
                            </label>
                            <input
                                data-testid="admin-pin-input"
                                value={form.pin}
                                onChange={(e) =>
                                    setForm({ ...form, pin: e.target.value })
                                }
                                inputMode="numeric"
                                maxLength={6}
                                className="w-full bg-earth-50 border border-earth-200 rounded-xl px-4 py-3 outline-none focus:border-terracotta-500 text-2xl tracking-[0.4em] text-center"
                            />
                        </div>
                    )}

                    <button
                        type="submit"
                        data-testid="admin-login-submit-btn"
                        disabled={loading}
                        className="btn-primary w-full mt-2"
                    >
                        {loading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            t.admin.login
                        )}
                    </button>
                    <div className="text-center pt-2">
                        <Link
                            to="/admin/reset-password"
                            data-testid="forgot-password-link"
                            className="text-xs uppercase tracking-wider text-earth-500 hover:text-terracotta-600 font-bold"
                        >
                            {t.admin.forgotPassword}
                        </Link>
                    </div>
                </form>
            </div>
            <div className="mt-8">
                <DevBranding variant="footer" />
            </div>
        </div>
    );
};

export default AdminLogin;
